import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  InfoOutlined,
  LoginRounded,
  MoreVertRounded,
  RefreshRounded,
  StorageRounded,
  SchoolRounded,
  MenuBookRounded,
  ApartmentRounded,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/auth.context';
import { useTenantStore } from '../../stores/tenant.store';
import { SeminaryRequestsPage } from './seminary-requests.page';
import { InvitesTab } from './platform/InvitesTab';
import { AuditTab } from './platform/AuditTab';
import { TenantDetailDrawer } from './platform/TenantDetailDrawer';
import {
  platformApi,
  type PlatformOverview,
  type TenantOverviewRow,
  type TenantStatus,
} from '../../lib/api/platform.api';
import { formatDate, formatDateTime, type DateLike } from '../../lib/i18n-format';

const STATUS_COLORS: Record<TenantStatus, 'success' | 'warning' | 'default'> = {
  active: 'success',
  suspended: 'warning',
  archived: 'default',
};

const LIFECYCLE_TARGETS: TenantStatus[] = ['active', 'suspended', 'archived'];

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function PlatformConsolePage() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const { user, isPlatformAdmin, loading: authLoading } = useAuth();
  const { currentTenantId, setCurrentTenant, setPlatformViewAs } = useTenantStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_VALUES = ['overview', 'invites', 'requests', 'audit'] as const;
  const rawTab = searchParams.get('tab') ?? 'overview';
  const tab = (TAB_VALUES as readonly string[]).includes(rawTab) ? rawTab : 'overview';
  const onTab = (v: string) => setSearchParams(v === 'overview' ? {} : { tab: v });

  const [data, setData] = useState<PlatformOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<TenantOverviewRow | null>(null);
  const [working, setWorking] = useState(false);
  const [detailRow, setDetailRow] = useState<TenantOverviewRow | null>(null);

  /** El formato lo resuelve el helper desde i18n; memorizarlo acá volvía a fijar
   *  el idioma a mano. */
  const dateFmt = (v: DateLike) =>
    formatDate(v, { day: '2-digit', month: 'short', year: 'numeric' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await platformApi.getOverview());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPlatformAdmin) void load();
  }, [isPlatformAdmin, load]);

  function enterTenant(row: TenantOverviewRow) {
    setPlatformViewAs({
      tenantId: row.id,
      tenantName: row.name,
      homeTenantId: currentTenantId,
    });
    // platformAdmin already passes every tenant read rule (firestore.rules:
    // isTenantMember → isPlatformAdmin). The synthetic membership only drives
    // the role-based UI; the claim is what authorizes reads.
    setCurrentTenant(row.id, {
      uid: user?.uid ?? '',
      tenantId: row.id,
      roles: ['tenantAdmin'],
      status: 'active',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
    void platformApi.logImpersonation(row.id, row.name, 'start');
    navigate('/reportes');
  }

  async function changeStatus(row: TenantOverviewRow, status: TenantStatus) {
    setMenuAnchor(null);
    setWorking(true);
    try {
      await platformApi.setTenantStatus(row.id, status);
      await load();
    } catch {
      setError(true);
    } finally {
      setWorking(false);
    }
  }

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <Box p={{ xs: 2, sm: 4 }}>
        <Alert severity="error">{t('noAccess')}</Alert>
      </Box>
    );
  }

  const totals = data?.totals;

  return (
    <Box p={{ xs: 2, sm: 4 }}>
      <Typography variant="h4" fontWeight={800} mb={2}>
        {t('title')}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => onTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="overview" label={t('tabs.overview')} />
        <Tab value="invites" label={t('tabs.invites')} />
        <Tab value="requests" label={t('tabs.requests')} />
        <Tab value="audit" label={t('tabs.audit')} />
      </Tabs>

      {tab === 'requests' ? (
        <SeminaryRequestsPage embedded />
      ) : tab === 'invites' ? (
        <InvitesTab />
      ) : tab === 'audit' ? (
        <AuditTab />
      ) : (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              {t('subtitle')}
            </Typography>
            <Tooltip title={t('refresh')}>
              <span>
                <IconButton onClick={() => void load()} disabled={loading || working}>
                  <RefreshRounded />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* Incidente de facturación del proveedor de IA (lib/ai-incident en
              functions): un aviso por incidente, con desde cuándo y cuántas
              veces. Se muestra la semana siguiente al último fallo. */}
          {data?.aiBillingIncident &&
            Date.now() - new Date(data.aiBillingIncident.lastAt).getTime() < 7 * 24 * 3_600_000 && (
              <Alert
                severity={
                  Date.now() - new Date(data.aiBillingIncident.lastAt).getTime() < 24 * 3_600_000
                    ? 'error'
                    : 'info'
                }
                sx={{ mb: 2 }}
              >
                <strong>
                  {Date.now() - new Date(data.aiBillingIncident.lastAt).getTime() < 24 * 3_600_000
                    ? t('aiIncident.active')
                    : t('aiIncident.ended')}
                </strong>{' '}
                {t('aiIncident.details', {
                  first: formatDateTime(data.aiBillingIncident.firstAt),
                  last: formatDateTime(data.aiBillingIncident.lastAt),
                  count: data.aiBillingIncident.count,
                  functions: Object.entries(data.aiBillingIncident.byFunction)
                    .map(([fn, n]) => `${fn} (${n})`)
                    .join(', '),
                })}
              </Alert>
            )}

          {/* Summary cards */}
          <Box
            display="grid"
            gridTemplateColumns={{ xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
            gap={2}
            mb={3}
          >
            <SummaryCard
              icon={<ApartmentRounded />}
              label={t('cards.tenants')}
              value={totals ? String(totals.tenants) : '—'}
              hint={
                totals
                  ? t('cards.tenantsBreakdown', {
                      active: totals.active,
                      suspended: totals.suspended,
                      archived: totals.archived,
                    })
                  : ''
              }
            />
            <SummaryCard
              icon={<SchoolRounded />}
              label={t('cards.students')}
              value={totals ? String(totals.students) : '—'}
            />
            <SummaryCard
              icon={<MenuBookRounded />}
              label={t('cards.courses')}
              value={totals ? String(totals.courses) : '—'}
            />
            <SummaryCard
              icon={<StorageRounded />}
              label={t('cards.storage')}
              value={totals ? formatBytes(totals.storageBytes) : '—'}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('loadError')}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : data && data.tenants.length > 0 ? (
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('table.tenant')}</TableCell>
                    <TableCell>{t('table.status')}</TableCell>
                    <TableCell align="right">{t('table.students')}</TableCell>
                    <TableCell align="right">{t('table.courses')}</TableCell>
                    <TableCell align="right">{t('table.sections')}</TableCell>
                    <TableCell align="right">{t('table.enrollments')}</TableCell>
                    <TableCell align="right">{t('table.storage')}</TableCell>
                    <TableCell>{t('table.lastActivity')}</TableCell>
                    <TableCell>{t('table.created')}</TableCell>
                    <TableCell align="right">{t('table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.tenants.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Stack>
                          <Typography variant="body2" fontWeight={600}>
                            {row.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.slug}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={STATUS_COLORS[row.status]}
                          label={t(`status.${row.status}`)}
                        />
                      </TableCell>
                      <TableCell align="right">{row.counts.students}</TableCell>
                      <TableCell align="right">{row.counts.courses}</TableCell>
                      <TableCell align="right">{row.counts.sections}</TableCell>
                      <TableCell align="right">{row.counts.enrollments}</TableCell>
                      <TableCell align="right">{formatBytes(row.storageBytes)}</TableCell>
                      <TableCell>
                        {row.lastActivityAt ? dateFmt(row.lastActivityAt) : '—'}
                      </TableCell>
                      <TableCell>{row.createdAt ? dateFmt(row.createdAt) : '—'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={t('actions.detail')}>
                            <IconButton size="small" onClick={() => setDetailRow(row)}>
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LoginRounded />}
                            onClick={() => enterTenant(row)}
                          >
                            {t('actions.enter')}
                          </Button>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setMenuAnchor(e.currentTarget);
                              setMenuRow(row);
                            }}
                          >
                            <MoreVertRounded fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Typography color="text.secondary" py={4}>
              {t('empty')}
            </Typography>
          )}

          {/* Lifecycle menu */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {LIFECYCLE_TARGETS.filter((s) => s !== menuRow?.status).map((status) => (
              <MenuItem
                key={status}
                disabled={working}
                onClick={() => menuRow && void changeStatus(menuRow, status)}
              >
                {t(`actions.setStatus.${status}`)}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      <TenantDetailDrawer
        tenantId={detailRow?.id ?? null}
        tenantName={detailRow?.name ?? ''}
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
      />
    </Box>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1} color="primary.main">
        {icon}
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" fontWeight={800}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
}
